// LiveStreamChatroom.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import media, { mediaHeight } from '../style/media.js';
import ToBottomIconSrc from '../../img/ic-to-down.svg';
import LiveStreamChatroomMessage from '../container/LiveStreamChatroomMessage.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

const CHECK_QUEUE_MILLISECONDS = 1000; // TODO: remote config
const MESSAGE_CHUNKS = 4; // TODO: remote config
const SCROLL_THRESHOLD = 32; // px - threshold for detecting if user is at bottom(one line height)
const USER_INTERACTION_LOCK_MS = 1000; // ms - lock auto-scroll after user interaction

export class LiveStreamChatroom extends React.PureComponent {
  chatroomRef = React.createRef();
  chatMessageRef = React.createRef();
  queuedMessages = new Set();
  chatMessagesQueue = [];
  isAtBottom = true;
  lastUserInteractionTime = 0;
  requestAnimationFrameId = null;
  batchSize = 1;
  state = {
    shouldShowToBottom: false,
    shownChatIds: [],
  };

  enqueueNewChatIds = chatIds => {
    chatIds.forEach(id => {
      if (this.queuedMessages.has(id)) {
        return;
      }

      this.queuedMessages.add(id);
      this.chatMessagesQueue.push(id);
    });
  };

  componentDidMount() {
    this.handleScrollToBottom();

    const { chatIds } = this.props;
    if (chatIds && chatIds.length > 0) {
      this.enqueueNewChatIds(chatIds);
    }
    this.handleChatMessagesQueue();

    this.chatMessagesQueueTick = setInterval(
      this.handleChatMessagesQueue,
      CHECK_QUEUE_MILLISECONDS
    );
  }
  componentDidUpdate(prevProps, prevState) {
    const { meId, latestMessageId, latestMessageSenderId, chatIds } =
      this.props;
    const { shownChatIds } = this.state;

    if (latestMessageId !== prevProps.latestMessageId) {
      this.chatMessagesTick = setTimeout(() => {
        this.enqueueNewChatIds(chatIds);
      }, 0);
    }

    if (shownChatIds !== prevState.shownChatIds) {
      const isLatestMessageSentFromMe =
        latestMessageSenderId && meId && latestMessageSenderId === meId;

      if (isLatestMessageSentFromMe || this.shouldScrollToBottom()) {
        this.nextTick = setTimeout(() => {
          this.handleScrollToBottom();
        }, 0);
      } else if (!this.isAtBottom) {
        this.setState({ shouldShowToBottom: true });
      }
    }
  }
  componentWillUnmount() {
    clearTimeout(this.nextTick);
    clearTimeout(this.chatMessagesTick);
    clearInterval(this.chatMessagesQueueTick);
    if (this.requestAnimationFrameId) {
      cancelAnimationFrame(this.requestAnimationFrameId);
    }
  }
  handleOnMouseEnter = () => {
    const { setLiveStreamChatroom } = this.props;
    setLiveStreamChatroom({ isScrolling: true });
  };
  handleOnMouseLeave = () => {
    const { setLiveStreamChatroom } = this.props;
    setLiveStreamChatroom({ isScrolling: false });
  };
  handleTouchStart = () => {
    const { setLiveStreamChatroom } = this.props;
    setLiveStreamChatroom({ isScrolling: true });
    this.lastUserInteractionTime = Date.now();
  };
  handleTouchEnd = () => {
    const { setLiveStreamChatroom } = this.props;
    setLiveStreamChatroom({ isScrolling: false });
  };
  handleMouseDown = () => {
    const { setLiveStreamChatroom } = this.props;
    setLiveStreamChatroom({ isScrolling: true });
    this.lastUserInteractionTime = Date.now();
  };
  handleMouseUp = () => {
    const { setLiveStreamChatroom } = this.props;
    setLiveStreamChatroom({ isScrolling: false });
  };
  handleWheel = () => {
    this.lastUserInteractionTime = Date.now();
  };
  shouldScrollToBottom = () => {
    const userLastInteractionTime = Date.now() - this.lastUserInteractionTime;
    return (
      this.isAtBottom && userLastInteractionTime > USER_INTERACTION_LOCK_MS
    );
  };
  handleScroll = () => {
    // Skip if already scheduled
    if (this.requestAnimationFrameId) return;

    this.requestAnimationFrameId = requestAnimationFrame(() => {
      this.requestAnimationFrameId = null;

      const parent = this.chatroomRef.current;
      if (!parent) return;

      let isAtBottom;
      const child = this.chatMessageRef.current;

      if (child) {
        // Use chatMessageRef
        const parentBottom = parent.scrollTop + parent.clientHeight;
        const childTop = child.offsetTop;

        isAtBottom = childTop < parentBottom;
      } else {
        // Fallback: calculate directly from scroll position
        const { scrollTop, scrollHeight, clientHeight } = parent;
        isAtBottom =
          scrollHeight - scrollTop - clientHeight <
          SCROLL_THRESHOLD * this.batchSize;
      }

      this.isAtBottom = isAtBottom;

      // Only hide "new message" button when user scrolls back to bottom
      if (isAtBottom && this.state.shouldShowToBottom) {
        this.setState({ shouldShowToBottom: false });
      }
    });
  };
  handleScrollToBottom = () => {
    if (!this.chatroomRef.current) return;

    const { clientHeight, scrollHeight } = this.chatroomRef.current;
    this.chatroomRef.current.scrollTo({
      top: scrollHeight - clientHeight,
      behavior: 'smooth',
    });

    this.isAtBottom = true;
    this.setState({ shouldShowToBottom: false });
  };
  handleChatMessagesQueue = () => {
    if (!this.chatMessagesQueue.length) {
      return;
    }
    const { chatListLimitation } = this.props;
    const batchSize = Math.ceil(this.chatMessagesQueue.length / MESSAGE_CHUNKS);
    const messagesToShow = this.chatMessagesQueue.splice(0, batchSize);
    const newShownChatIds = [
      ...this.state.shownChatIds,
      ...messagesToShow,
    ].slice(-chatListLimitation || 0);
    this.setState({
      shownChatIds: newShownChatIds,
    });
    this.batchSize = batchSize;
  };
  render() {
    const {
      t,
      streamId,
      chatScrollDetectItemCount,
      isPopOut,
      isMiniProfileOpen,
    } = this.props;
    const { shouldShowToBottom, shownChatIds } = this.state;
    return (
      <StyledLiveStreamChatroom className="swiper-no-swiping">
        <MessageContainer
          ref={this.chatroomRef}
          onMouseEnter={this.handleOnMouseEnter}
          onMouseLeave={this.handleOnMouseLeave}
          onTouchStart={this.handleTouchStart}
          onTouchEnd={this.handleTouchEnd}
          onWheel={this.handleWheel}
          onScroll={this.handleScroll}
          hasChat={shownChatIds.length > 0}
          isPopOut={isPopOut}
        >
          {shownChatIds.map((id, index) => {
            if (index === shownChatIds.length - chatScrollDetectItemCount) {
              return (
                <ChatScrollDetectionMessage key={id} ref={this.chatMessageRef}>
                  <LiveStreamChatroomMessage
                    chatMessageId={id}
                    streamId={streamId}
                    hasWidthParent={true}
                  />
                </ChatScrollDetectionMessage>
              );
            }

            return (
              <LiveStreamChatroomMessage
                key={id}
                chatMessageId={id}
                streamId={streamId}
              />
            );
          })}
        </MessageContainer>
        {shouldShowToBottom && (
          <ToBottomWrapper>
            <ToBottom
              onClick={this.handleScrollToBottom}
              isMiniProfileOpen={isMiniProfileOpen}
            >
              <ToBottomIcon src={ToBottomIconSrc} />
              {t('chat_new_message', {
                ns: TranslationNamespace.LIVESTREAM,
              })}
            </ToBottom>
          </ToBottomWrapper>
        )}
      </StyledLiveStreamChatroom>
    );
  }
}

LiveStreamChatroom.propTypes = {
  streamId: PropTypes.string,
  meId: PropTypes.string,
  latestMessageId: PropTypes.string,
  latestMessageSenderId: PropTypes.string,
  chatListLimitation: PropTypes.number,
  chatIds: PropTypes.array,
  t: PropTypes.func,
  setLiveStreamChatroom: PropTypes.func,
  chatScrollDetectItemCount: PropTypes.number,
  isPopOut: PropTypes.bool,
  isMiniProfileOpen: PropTypes.bool,
};

LiveStreamChatroom.defaultProps = {
  streamId: null,
  meId: '',
  latestMessageId: null,
  latestMessageSenderId: null,
  chatListLimitation: 0,
  chatIds: [],
  t: () => null,
  setLiveStreamChatroom: () => null,
  chatScrollDetectItemCount: 1,
  isPopOut: false,
  isMiniProfileOpen: false,
};

const StyledLiveStreamChatroom = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;

  ${mediaHeight.mobileLandscape`
    @media (orientation: landscape) and (hover: none) {
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
    }
  `};
`;

const MessageContainer = styled.div`
  position: relative;
  width: calc(100% + 40px);
  max-height: 100%;
  margin-top: 0px;
  margin-bottom: 4px;
  padding-right: 40px;
  overflow-y: scroll;
  overscroll-behavior-y: contain;
  ${media.tablet`
    max-height: ${({ isPopOut }) => (isPopOut ? '100%' : '150px')};
  `}

  ${mediaHeight.mobileLandscape`
    @media (orientation: landscape) and (hover: none) {
      max-height: 80px;
    }
  `};

  ::-webkit-scrollbar {
    display: none;
  }
`;

const ToBottomWrapper = styled.div`
  width: 100%;
`;

const ToBottom = styled.button`
  ${ResetButtonStyle};
  display: flex;
  align-items: center;
  position: absolute;
  bottom: ${({ isMiniProfileOpen }) => (isMiniProfileOpen ? '36px' : '8px')};
  left: 50%;
  z-index: 2;
  transform: translateX(-50%);
  font-size: 14px;
  padding: 8px 12px;
  border-radius: 15px;
  color: #ffffff;
  background-color: rgba(96, 196, 232, 0.8);
  cursor: pointer;
`;

const ToBottomIcon = styled.img`
  width: 14px;
  height: 14px;
  margin-right: 4px;
`;

const ChatScrollDetectionMessage = styled.div`
  width: 100%;
  ${media.tablet`
    width: 88%;
  `}
`;

export default withTranslation()(LiveStreamChatroom);
